import { Box, Divider, Group, Select } from '@byecode/ui'
import type { DataSourceAbstract, FieldProtocol, IndependentAction } from '@lighthouse/core'
import type { CreateSingleRecordActionConfig, FlowNode, NodeTypes } from '@lighthouse/shared'
import {
    getIsIncludesDisableDataSourceInWorkFlow,
    getTableIcon,
    ListItem4ByecodeUi,
    nodeTypeOptions,
    OUTPUT_SINGLE_DATA_NODES,
    SelectDataSource
} from '@lighthouse/shared'
import React, { useCallback, useMemo } from 'react'

import { SettingRecordContent } from '@/containers/FlowDetail/components/SettingRecordContent'
import { promptSwitchDataSource } from '@/containers/FlowDetail/utils/promptSwitchDataSource'
import { useFlowContainerContext } from '@/contexts/FlowContext'
import { useCurrentAppId, useCurrentEnvId } from '@/hooks/useApplication'
import { useDataSourceList } from '@/hooks/useDataSource'

import OutputVariable from '../../../OutputVariable'
import * as SC from '../../styles'

interface CreateSingleRecordActionConfigureProps {
    nodeType?: NodeTypes
    value: CreateSingleRecordActionConfig
    allParentActions?: IndependentAction[]
    allParentNodes?: FlowNode[]
    actionTypeSwitcher?: React.ReactNode
    onChange: (value: CreateSingleRecordActionConfig) => void
}

export const CreateSingleRecordActionConfigure: React.FC<CreateSingleRecordActionConfigureProps> = ({
    allParentActions,
    allParentNodes,
    actionTypeSwitcher,
    nodeType,
    value,
    onChange
}) => {
    const appId = useCurrentAppId()
    const envId = useCurrentEnvId()
    const listDs = useDataSourceList(appId, envId)
    const { type, elRef } = useFlowContainerContext()
    const { dataSourceId, fields } = value
    const isInWorkflow = !!type
    const selectDatasourceList = useMemo(
        () =>
            listDs.reduce<DataSourceAbstract[]>((listDs, item) => {
                const tableIcon = getTableIcon(item)
                if (!getIsIncludesDisableDataSourceInWorkFlow(item)) {
                    listDs.push(item)
                }
                return listDs
            }, []),
        [listDs]
    )

    const handleChangeDataSource = useCallback(
        async (val: string) => {
            if (val === dataSourceId) {
                return
            }
            const isConfirmed = !dataSourceId || !isInWorkflow ? true : await promptSwitchDataSource(elRef?.current)
            if (isConfirmed) {
                onChange({ ...value, dataSourceId: val, fields: [] })
            }
        },
        [dataSourceId, elRef, isInWorkflow, onChange, value]
    )

    const allSingleParentNodes = useMemo(
        () => allParentNodes?.filter(item => OUTPUT_SINGLE_DATA_NODES.has(item.data.nodeType)),
        [allParentNodes]
    )

    const outputOptions = useMemo(() => {
        const current: Record<string, FieldProtocol> = selectDatasourceList.find(item => item.id === dataSourceId)?.schema || {}

        const values = Object.values(current)

        return values.map(item => ({
            id: item.id,
            label: item.name,
            desc: '记录的字段值',
            type: item.innerType
        }))
    }, [selectDatasourceList, dataSourceId])

    return (
        <SC.Container>
            <Group label="动作配置">
                <ListItem4ByecodeUi>
                    <SC.FormItemLabelWrapper>
                        <SC.FormItemLabel>类型</SC.FormItemLabel>
                    </SC.FormItemLabelWrapper>
                    <SC.FormItemContent>
                        {actionTypeSwitcher || <Select disabled value={nodeType} options={nodeTypeOptions} />}
                    </SC.FormItemContent>
                </ListItem4ByecodeUi>
                <ListItem4ByecodeUi>
                    <SC.FormItemLabelWrapper>
                        <SC.FormItemLabel required>选择数据表</SC.FormItemLabel>
                    </SC.FormItemLabelWrapper>
                    <SC.FormItemContent>
                        <SelectDataSource
                            value={dataSourceId}
                            onChange={handleChangeDataSource}
                            searchable
                            withinPortal
                            placeholder="请选择数据表"
                            dataSourceList={selectDatasourceList}
                        />
                    </SC.FormItemContent>
                </ListItem4ByecodeUi>
            </Group>
            {dataSourceId && (
                <>
                    <Divider color="var(--color-gray-200)" />
                    <Group label={<SC.Label>设置记录内容</SC.Label>}>
                        <Box px={8}>
                            <SettingRecordContent
                                allParentNodes={allParentNodes}
                                upstreamNodes={allSingleParentNodes}
                                allParentActions={allParentActions}
                                dataSourceId={dataSourceId}
                                value={fields}
                                onChange={val => onChange({ ...value, fields: val })}
                            />
                        </Box>
                    </Group>
                </>
            )}
            <OutputVariable options={outputOptions} />
        </SC.Container>
    )
}
